/**
 * PEStart Archiv — Snapshots in clientData.peStartArchiv (Firebase/Local Sync).
 */

import { generateSimpleId } from '../../store/utils';
import type { PEStartState } from '../../store/usePEStartStore';
import {
    createDefaultPEStartState,
    normalizeGeneratedSections,
    resolveKfWFieldsFromState,
} from '../../store/usePEStartStore';

export type PEStartKennzahlSnapshot = {
    planumsatzNetto: number;
    totpunktMonatBrutto: number;
    unternehmerlohn: number;
    startFinanzbedarf: number;
    kreditbetrag: number;
    rateMonat: number;
    laufzeitJahre: number;
    zinssatzProzent: number;
    pufferMonate: number;
    breakEvenMonat: number | null;
    foundingType: PEStartState['foundingType'];
    monate: Array<{
        month: number;
        umsatzZiel: number;
        umsatzPrognose: number;
    }>;
};

export type PEStartArchivStatus = 'gespeichert' | 'entwurf' | 'bankreif';

export type PEStartArchivEintrag = {
    id: string;
    timestamp: number;
    titel: string;
    note?: string;
    /** Anzeige-Status in der PEStart-Übersicht */
    status: PEStartArchivStatus;
    /** Arbeitsstand zum Speicherzeitpunkt */
    state: PEStartState;
    /** Eingefrorene Kennzahlen für späteren Vergleich / Bank */
    kennzahlen: PEStartKennzahlSnapshot;
};

export function normalizeArchivStatus(raw: unknown): PEStartArchivStatus {
    if (raw === 'entwurf' || raw === 'bankreif' || raw === 'gespeichert') return raw;
    return 'gespeichert';
}

export function archivStatusLabel(status: PEStartArchivStatus): string {
    if (status === 'entwurf') return 'Entwurf';
    if (status === 'bankreif') return 'Bankreif';
    return 'Gespeichert';
}

export function foundingTypeLabel(type: PEStartState['foundingType'] | string): string {
    return type === 'TAKEOVER' ? 'Übernahme' : 'Neugründung';
}

export function normalizePEStartArchiv(raw: unknown): PEStartArchivEintrag[] {
    if (!Array.isArray(raw)) return [];
    return raw
        .filter((e) => e && typeof e === 'object' && typeof (e as any).id === 'string')
        .map((e: any) => ({
            id: String(e.id),
            timestamp: Number(e.timestamp) || Date.now(),
            titel: String(e.titel || 'PEStart-Stand').trim() || 'PEStart-Stand',
            ...(typeof e.note === 'string' && e.note.trim() ? { note: e.note.trim() } : {}),
            status: normalizeArchivStatus(e.status),
            state: {
                ...createDefaultPEStartState(),
                ...(e.state && typeof e.state === 'object' ? e.state : {}),
                investments: {
                    ...createDefaultPEStartState().investments,
                    ...(e.state?.investments || {}),
                    items: Array.isArray(e.state?.investments?.items)
                        ? e.state.investments.items
                        : [],
                },
                loanPlan: {
                    ...createDefaultPEStartState().loanPlan,
                    ...(e.state?.loanPlan || {}),
                },
                staffFlags:
                    e.state?.staffFlags && typeof e.state.staffFlags === 'object'
                        ? e.state.staffFlags
                        : {},
                subsidies: Array.isArray(e.state?.subsidies) ? e.state.subsidies : [],
                generatedSections: normalizeGeneratedSections(e.state?.generatedSections),
                ...resolveKfWFieldsFromState(e.state),
            },
            kennzahlen: normalizeKennzahlen(e.kennzahlen),
        }));
}

function normalizeKennzahlen(k: any): PEStartKennzahlSnapshot {
    const defaults = createDefaultPEStartState();
    return {
        planumsatzNetto: Number(k?.planumsatzNetto) || 0,
        totpunktMonatBrutto: Number(k?.totpunktMonatBrutto) || 0,
        unternehmerlohn: Number(k?.unternehmerlohn) || 0,
        startFinanzbedarf: Number(k?.startFinanzbedarf) || 0,
        kreditbetrag: Number(k?.kreditbetrag) || 0,
        rateMonat: Number(k?.rateMonat) || 0,
        laufzeitJahre: Number(k?.laufzeitJahre) || defaults.loanPlan.laufzeitJahre,
        zinssatzProzent: Number(k?.zinssatzProzent) || defaults.loanPlan.zinssatzProzent,
        pufferMonate: Number(k?.pufferMonate) || defaults.loanPlan.pufferMonate,
        breakEvenMonat:
            k?.breakEvenMonat === null || k?.breakEvenMonat === undefined
                ? null
                : Number(k.breakEvenMonat) || null,
        foundingType: k?.foundingType === 'TAKEOVER' ? 'TAKEOVER' : 'NEW',
        monate: Array.isArray(k?.monate)
            ? k.monate.map((m: any) => ({
                  month: Number(m?.month) || 0,
                  umsatzZiel: Number(m?.umsatzZiel) || 0,
                  umsatzPrognose: Number(m?.umsatzPrognose) || 0,
              }))
            : [],
    };
}

export function createPEStartArchivEintrag(
    titel: string,
    state: PEStartState,
    kennzahlen: PEStartKennzahlSnapshot,
    note?: string,
    status: PEStartArchivStatus = 'gespeichert'
): PEStartArchivEintrag {
    return {
        id: generateSimpleId(),
        timestamp: Date.now(),
        titel: titel.trim() || `PEStart ${new Date().toLocaleDateString('de-DE')}`,
        ...(note?.trim() ? { note: note.trim() } : {}),
        status: normalizeArchivStatus(status),
        state: JSON.parse(JSON.stringify(state)) as PEStartState,
        kennzahlen: JSON.parse(JSON.stringify(kennzahlen)) as PEStartKennzahlSnapshot,
    };
}

/**
 * Entfernt einen Archiv-Eintrag inkl. zugehöriger KI-Texte aus clientData.
 * Rückgabe: Partial-Patch für setClientData.
 */
export function buildPEStartArchivDeletePatch(
    prev: Record<string, unknown> | null | undefined,
    id: string
): { peStartArchiv: PEStartArchivEintrag[]; peStartGeneratedTexts: Record<string, unknown> } {
    const peStartArchiv = normalizePEStartArchiv(prev?.peStartArchiv).filter((e) => e.id !== id);
    const textsRaw =
        prev?.peStartGeneratedTexts && typeof prev.peStartGeneratedTexts === 'object'
            ? { ...(prev.peStartGeneratedTexts as Record<string, unknown>) }
            : {};
    if (id in textsRaw) delete textsRaw[id];
    return { peStartArchiv, peStartGeneratedTexts: textsRaw };
}
